import {
  useAccountQuestion,
  useGetAccountInfo,
  usePostAccountQuestion,
} from '@frontend/sorghum/data-access';
import { queryClient } from '@frontend/sorghum/external-providers';
import {
  PostAccountQuestionProps,
  RegistrationSourceEnum,
  SorghumFeatureMicroFormValue,
} from '@frontend/sorghum/interface';
import {
  PAGE_LOGIN,
  PAGE_MAIN,
  PAGE_PAGES,
  PAGE_SELECT_CHANNEL,
  sendConversionEvent,
  sendGAEvent,
  useDocumentTitle,
  usePath,
} from '@frontend/sorghum/utils';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

const industries = [
  'Tech',
  'Retail',
  'Luxury',
  'FMCG',
  'GOV/NGO',
  'EC',
  'Media & Entertainment',
  'Agency',
  'F&B',
  'Others',
];

/**
 * 受邀加入既有專案的團隊成員（邀請連結註冊）不算廣告轉換的「註冊完成」：
 * malasun 對邀請進來的新帳號 step 一樣從 QUESTION 開始、會走這個問卷頁，
 * 不過濾的話同事被拉進專案也會被 OpenAI Ads 算成一次廣告帶來的註冊。
 */
export const isInvitedRegistration = (registrationSource?: number) =>
  registrationSource === RegistrationSourceEnum.PROJECT_INVITATION;

export const useSignupMicro = () => {
  const [t] = useTranslation();
  const { navigate } = usePath();
  const token = localStorage.getItem('token');
  const [searchParams] = useSearchParams();
  const projectID = searchParams.get('p');
  const urlP = projectID ? `/${projectID}` : '';
  const queryP = projectID ? `?p=${projectID}` : '';
  const { data: account, isFetched } = useGetAccountInfo();
  const { data: question } = useAccountQuestion();

  const { mutate: sendMicro, isLoading: isSendingMicro } =
    usePostAccountQuestion();

  const industryItems = useMemo(
    () =>
      industries.map((item, index) => {
        return {
          label: t(`micro.industry.${item}`),
          value: index.toString(),
        };
      }),
    [t],
  );
  const submit = (data: SorghumFeatureMicroFormValue) => {
    const props: PostAccountQuestionProps = {
      companyName: data.company,
      email: data.email,
      phone: data.phone,
    };

    sendMicro(props, {
      onSuccess: () => {
        queryClient.setQueryData(['get-account-info'], {
          userName: question?.userName || '',
          step: 'choose_project',
        });
        sendGAEvent(
          'Survey',
          'Survey submit success',
          'Survey - Survey submit - success',
          '',
        );
        // 廣告轉換「註冊完成」定義在這裡而不是填完 email 密碼：
        // Email / FB 兩種註冊路徑都必經此處，且 Email 路徑到此已通過驗證信，數字不會被假信箱灌水
        if (!isInvitedRegistration(account?.registrationSource)) {
          sendConversionEvent('registration_completed');
        }
        queryClient.invalidateQueries(['get-account-info']);
      },
    });
  };

  useEffect(() => {
    if (isFetched) {
      if (!token) {
        navigate(PAGE_LOGIN);
      } else if (account?.step === 'fb_connect') {
        navigate(PAGE_PAGES + queryP);
      } else if (account?.step === 'choose_project') {
        navigate(PAGE_SELECT_CHANNEL + queryP);
      } else if (account?.step === 'complete') {
        navigate(PAGE_MAIN);
      }
    }
  }, [token, account, navigate, projectID, urlP, queryP, isFetched]);

  useDocumentTitle(t('title.micro'));

  return {
    pageName: question?.pageName || '',
    userName: question?.userName || '',
    email: account?.userName || '',
    industryItems,
    submit,
    isSendingMicro,
  };
};
